import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { customAlphabet } from "nanoid";
import { useShowToast } from "./notice/use-show-toast";
import {
  CANVAS_HEIGHT_PX,
  CANVAS_WIDTH_PX,
  SECTION_DEFAULT_TITLE,
  clampFrameToCanvas,
  clampPointToCanvas,
  TEXT_BODY_MAX_LENGTH,
  type CanvasObject,
  type StrokePattern,
} from "@refinement-canvas/domain";
import { useCanvasStore, type ShapeKind } from "./store/canvas-store";
import { CanvasGrid } from "./canvas-grid";
import { PdfBoundaryOverlay } from "./pdf-boundary-overlay";
import { CanvasObjectsLayer } from "./canvas-objects-layer";
import { CanvasPenLayer, type PenLayerHandle } from "./canvas-pen-layer";
import { CanvasCursorsLayer } from "./canvas-cursors-layer";
import { CanvasSelectionOverlay } from "./canvas-selection-overlay";
import { CanvasAlignmentGuidesLayer } from "./canvas-alignment-guides-layer";
import { CanvasConnectorOverlay } from "./canvas-connector-overlay";
import { CanvasMarqueeOverlay } from "./canvas-marquee-overlay";
import { CanvasShapePlacementOverlay } from "./canvas-shape-placement-overlay";
import { CanvasRemoteSelectionsLayer } from "./canvas-remote-selections-layer";
import { CanvasRemotePreviewsLayer } from "./canvas-remote-previews-layer";
import { CanvasRemoteCaretsLayer } from "./canvas-remote-carets-layer";
import { screenToStage } from "../../lib/canvas-coordinates";
// グリッド吸着と配置既定サイズは、コネクタからのオブジェクト自動作成（connector-auto-create.ts）と
// 同じ規則・同じ値を使うため共有モジュールから読み込む。
import { snapPointIfGridEnabled } from "./grid-snap";
import { isNodeOnCanvasSurface } from "./canvas-surface-hit";
import {
  TABLE_DEFAULT_COLUMNS,
  TABLE_DEFAULT_ROWS,
  TEXT_DEFAULT_H,
  textInitialHeight,
  TEXT_DEFAULT_W,
  placementSizeKeyOf,
  resolvePlacementSize,
} from "./object-placement-defaults";
import { useKeyboardPan } from "./input/use-keyboard-pan";
import { useWheelPanZoom } from "./input/use-wheel-pan-zoom";
import { useTouchPinchZoom } from "./input/use-touch-pinch-zoom";
import { usePasteHandler, performClipboardPaste } from "./input/use-paste-handler";
import { planLinkifySelectedObjects } from "./input/linkify-selected-objects";
import { EmptyAreaContextMenu } from "./controls/empty-area-context-menu";
import {
  PENDING_ATTACHMENT_SRC,
  generateImageLqipDataUrl,
  isTransientAttachmentObject,
} from "./attachment-placeholder";
import { describeUploadError, putToPresignedUrl, validateVideoUpload } from "./upload-limits";
import { prepareImageForUpload } from "./image-resize";
import { useDropHandler } from "./input/use-drop-handler";
import { usePointerMode } from "./input/use-pointer-mode";
import { useMarqueeSelection } from "./input/use-marquee-selection";
import { useGlobalShortcuts } from "./input/use-global-shortcuts";
import { isEditingTarget } from "./input/editing-target";
import {
  clearCanvasHistory,
  type CanvasObjectDraft,
  type CanvasOpDispatcher,
} from "./use-canvas-op-dispatcher";
import { cloneObjectAsDraft } from "../../lib/canvas-object-clone";
import { useCursorBroadcaster } from "./use-cursor-broadcaster";
import { useCanvasPanningDocumentAttribute } from "./use-canvas-panning-attribute";
import { useCanvasApi, type CanvasApiClient } from "../../lib/canvas-api";
import type { StrokePoint } from "../../lib/pen-stroke";
import { loadCanvasView, saveCanvasView } from "../../lib/canvas-view-preference";
import { readCanvasViewFromUrl } from "../../lib/canvas-view-url";
import { replaceCanvasViewInUrl } from "../../lib/canvas-view-url-sync";
import { resolveInitialCanvasView } from "../../lib/canvas-initial-view";
import { useCanvasDocStore } from "./store/canvas-doc-store";
import { resolveShapeCreateStyle } from "../../lib/shape-preference";
import { resolveTextCreateStyle } from "../../lib/text-style-preference";
import { fitPastedTextForBudget, resolvePastedTextFrameSize } from "../../lib/paste-text-frame";
import { fitPastedTableCells, resolvePastedTableFrameSize } from "../../lib/table-paste-frame";
import { CLIENT_MESSAGE_MAX_BYTES } from "../../realtime/websocket-realtime";
import { textBodyFitNotice } from "./input/text-body-send-fit";
import { resolveArrowOriginFields, type ShapeDragFrame } from "../../lib/shape-arrow";
import type { PlaceableObjectKind } from "../../lib/last-placed-object-preference";
import { loadPlacementPreference } from "../../lib/placement-size-preference";
import { createAndRememberPlacement, type PlacementMemory } from "./remember-placement";
import { useCurrentActor } from "../../auth/use-current-actor";
import { isSnapshotAvatarImageUrl, sanitizeAvatarImageUrl } from "../../auth/avatar-image-url";
import { selectCreatedObject } from "./place-created-object";

interface Props {
  readonly canvasId: string;
  readonly dispatcher: CanvasOpDispatcher;
  readonly currentUserId: string;
  /**
   * 参照専用モード（Connect Note 埋め込み）。true のとき編集系の入力（配置・描画・貼り付け・
   * ドロップ・範囲選択・編集ショートカット）とカーソル配信・view 同期（localStorage / URL 反映）を
   * 無効化し、パン / ズームとリンク・コメントの閲覧操作だけを残す。
   */
  readonly readOnly?: boolean | undefined;
}

const IMAGE_DEFAULT_MAX = 480;
// パン/ズーム操作が止まったとみなすまでのアイドル時間（ms）。これを過ぎたら stage の
// will-change を解除し、現在のズーム倍率で再ラスタライズさせて文字・図形を鮮明に表示する。
const VIEW_INTERACTION_IDLE_MS = 200;
type ShowToast = ReturnType<typeof useShowToast>;
/** 仮寸法と実寸法の数値桁差や、将来の小さな封筒拡張を吸収する余裕。 */
const PASTE_TEXT_CREATE_MARGIN_BYTES = 64;

/** 配置直後の空の 3×3 表。呼び出しごとに新しい配列を作り、楽観 state 間で共有しない。 */
function createDefaultTableCells(): string[][] {
  return Array.from({ length: TABLE_DEFAULT_ROWS }, () =>
    new Array<string>(TABLE_DEFAULT_COLUMNS).fill("")
  );
}

// 10240x5760 サイズの DOM ステージ。
// CSS transform: translate3d(panX,panY,0) scale(zoom) で全オブジェクトを一括変形する。
// 子レイヤはステージ座標で配置し、ヒットテストは DOM のイベントバブリングで処理する。
export function CanvasStage({
  canvasId,
  dispatcher,
  currentUserId,
  readOnly = false,
}: Props): JSX.Element {
  const view = useCanvasStore((s) => s.view);
  const tool = useCanvasStore((s) => s.tool);
  const shapeKind = useCanvasStore((s) => s.shapeKind);
  const shapeFill = useCanvasStore((s) => s.shapeFill);
  const shapeStroke = useCanvasStore((s) => s.shapeStroke);
  const shapeStrokePattern = useCanvasStore((s) => s.shapeStrokePattern);
  const shapeStrokeWidth = useCanvasStore((s) => s.shapeStrokeWidth);
  const sectionFill = useCanvasStore((s) => s.sectionFill);
  const stickyColor = useCanvasStore((s) => s.stickyColor);
  const stampKind = useCanvasStore((s) => s.stampKind);
  const textFontSize = useCanvasStore((s) => s.textFontSize);
  const textAlign = useCanvasStore((s) => s.textAlign);
  const textColor = useCanvasStore((s) => s.textColor);
  const textFill = useCanvasStore((s) => s.textFill);
  const textBold = useCanvasStore((s) => s.textBold);
  const setView = useCanvasStore((s) => s.setView);
  const viewSyncArmSignal = useCanvasStore((s) => s.viewSyncArmSignal);
  // アバタースタンプ配置時に、現在のユーザーのアバター画像（ポータブル data URL 等）を
  // スナップショットするために参照する。
  const currentActor = useCurrentActor();
  const containerRef = useRef<HTMLDivElement>(null);
  const penLayerRef = useRef<PenLayerHandle | null>(null);
  const showToast = useShowToast();
  // view を URL クエリ / localStorage へ同期して良いか（=利用者が能動的に表示を動かしたか）を保持する。
  // 初回表示（プログラムによる復元・中央寄せ）だけでは同期せず、利用者が pan/zoom した時点で
  // 同期を開始する。これにより、(1) 新規作成直後の URL を `/c/<id>` のまま汚さない、
  // (2) 一時的な既定 view が localStorage に保存されて中央寄せ初期表示を打ち消す事故を防ぐ。
  // 共有 URL（?cx/cy/z 付き）からの遷移時も、URL には既に位置が載っているため初回同期は不要で、
  // 利用者が実際に pan/zoom した時点で armed 化される（全経路で同じ扱いにする）。
  const viewSyncArmedRef = useRef(false);
  // view が連続更新されるキーボードパン中も URL 同期を飢餓させないよう、
  // 次回反映待ちの rAF とその時点の最新 view を ref で保持する。
  const viewUrlSyncFrameRef = useRef<number | null>(null);
  const latestViewForUrlRef = useRef(view);
  // view 同期 arm シグナル（store の単調増加カウンタ）のベースライン。canvas の (再)初期化時点の
  // 値を記録し、これを「超えた」増分のときだけ arm する。これにより、CanvasStage が unmount/remount
  // される経路（canvas 切替で loadState.ready が一旦 false になる等）でも、前のキャンバスで増えた
  // シグナルを mount 直後に誤検出して arm してしまうのを防ぐ（初回既定 view の誤保存を避ける契約を守る）。
  const viewSyncArmBaselineRef = useRef(0);

  // パン/ズーム操作中かどうか。操作中だけ stage を GPU 合成レイヤへ昇格させ、静止後に解除する。
  const [isViewInteracting, setIsViewInteracting] = useState(false);
  const interactionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // state と二重持ちする操作中フラグ（effect 内で同期参照するため）と、最後に view が変化した時刻。
  const interactingRef = useRef(false);
  const lastViewChangeAtRef = useRef(0);

  const canvasApi = useCanvasApi();

  useEffect(() => {
    clearCanvasHistory();
    // canvas 切替時は同期可否をリセットする。CanvasStage は canvasId だけ変わって同一インスタンスで
    // 使い回される場合と、loadState の遷移で unmount/remount される場合の両方があるため、前のキャンバスで
    // armed になった状態を持ち越すと、次のキャンバスの初回プログラム的 setView まで即同期されてしまう。
    // ここで必ず false に戻し、さらに arm シグナルの現在値をベースラインへ記録して、これを超える増分
    // （= このキャンバスでの実操作）のみを arm 対象にする。
    viewSyncArmedRef.current = false;
    viewSyncArmBaselineRef.current = useCanvasStore.getState().viewSyncArmSignal;
    // 初期表示の決定規則そのものは lib/canvas-initial-view.ts の純粋関数にある
    // （URL のクエリ ?cx=...&cy=...&z=... を最優先し、次に編集ビューのみ localStorage の
    // 最終表示位置、いずれも無ければキャンバス中央・等倍）。ここでの責務は、window と
    // localStorage という副作用のある入力を集めて渡し、結果を setView することだけ。
    //
    // どの経路で決まった場合も、復元時点では同期しない（armed のまま false）。利用者が実際に
    // pan/zoom した時点で armed 化され、以降の同期が始まる。ここで先に armed=true にすると、
    // 復元 view 反映前の同一フラッシュで古い view を 1 度だけ保存/同期してしまうため。
    const { view: initialView } = resolveInitialCanvasView({
      fromUrl: readCanvasViewFromUrl(window.location.search),
      readOnly,
      // 参照専用（埋め込み）では純粋関数側がこのクロージャを呼ばないため、分離ストレージへ触れない。
      loadSavedView: () => loadCanvasView(canvasId),
      viewport: { width: window.innerWidth, height: window.innerHeight },
    });
    setView(initialView);
  }, [canvasId, setView, readOnly]);

  // 利用者が実際に「表示（view）を動かす」操作でのみ view 同期を有効化する。
  // 初回表示のプログラム的な setView や、アバタークリック等の UI 操作（→フォーカスモードの
  // プログラム的 setView）では同期しない。対象は次に限定する:
  //   - キャンバス本体上のホイール（pan/zoom）
  //   - キャンバス本体上の pointerdown（空白ドラッグでの pan 等）
  //   - WASD / カーソルキー（キーボード pan。修飾キー併用・入力要素フォーカス時は除外）
  // canvasId が変わると上の復元 effect が armed を false に戻すため、ここも canvasId 依存で
  // リスナを貼り直し、新しいキャンバスで再び arm 判定できるようにする。
  useEffect(() => {
    if (viewSyncArmedRef.current) return undefined;
    const arm = (): void => {
      viewSyncArmedRef.current = true;
    };
    const isInsideCanvas = (target: EventTarget | null): boolean =>
      isNodeOnCanvasSurface(containerRef.current, target);
    const onWheel = (event: WheelEvent): void => {
      if (isInsideCanvas(event.target)) arm();
    };
    const onPointerDown = (event: PointerEvent): void => {
      if (isInsideCanvas(event.target)) arm();
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      // WASD に加えてカーソルキーも view 同期の arm 対象にする（use-keyboard-pan と対応）。
      const PAN_KEYS = ["w", "a", "s", "d", "arrowup", "arrowdown", "arrowleft", "arrowright"];
      if (!PAN_KEYS.includes(event.key.toLowerCase())) return;
      if (isEditingTarget(event.target)) return;
      arm();
    };
    window.addEventListener("wheel", onWheel, { capture: true, passive: true });
    window.addEventListener("pointerdown", onPointerDown, { capture: true });
    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () => {
      window.removeEventListener("wheel", onWheel, { capture: true });
      window.removeEventListener("pointerdown", onPointerDown, { capture: true });
      window.removeEventListener("keydown", onKeyDown, { capture: true });
    };
  }, [canvasId]);

  // canvas-container の外側（コメント UI 上の Ctrl+ホイールズーム等）で view を動かした操作からの
  // arm 要求を受けて arm する。復元 effect が記録したベースライン（このキャンバスの (再)初期化時点の
  // シグナル値）を超えた増分のときだけ arm するため、unmount/remount や canvasId 切替で前のキャンバスの
  // シグナル値を持ち越しても、mount 直後に誤って arm することはない。
  useEffect(() => {
    if (viewSyncArmSignal > viewSyncArmBaselineRef.current) viewSyncArmedRef.current = true;
  }, [viewSyncArmSignal]);

  useEffect(() => {
    // 参照専用（埋め込み）では表示位置を保存しない（分離ストレージで例外になりうる・埋め込みを
    // 不変に保つ）。
    if (readOnly) return;
    // 利用者が能動的に表示を動かすまでは保存しない（初回の既定 view を保存して中央寄せを
    // 打ち消す事故を防ぐ）。
    if (!viewSyncArmedRef.current) return;
    saveCanvasView(canvasId, view);
  }, [canvasId, readOnly, view]);

  // view 変更を URL クエリへリアルタイム反映する（共有 URL 用）。
  // pan/zoom は短時間で多数発火するため、replaceState の連打を抑えるよう rAF 一括にまとめる。
  useEffect(() => {
    latestViewForUrlRef.current = view;
    // 参照専用（埋め込み）では URL を書き換えない（埋め込み URL を不変に保つ）。
    if (readOnly) return undefined;
    // 利用者が能動的に表示を動かすまでは URL を書き換えない。
    // これにより新規作成直後の URL は `/c/<id>` のまま保たれ、共有時のみ ?cx/cy/z が付く。
    if (!viewSyncArmedRef.current) return undefined;
    // 各 view 変更の cleanup で rAF をキャンセルすると、キーを押し続けるパンで
    // URL 反映が後回しになる。待機中の rAF は残し、callback 側が最新 view を反映する。
    if (viewUrlSyncFrameRef.current !== null) return undefined;
    viewUrlSyncFrameRef.current = window.requestAnimationFrame(() => {
      viewUrlSyncFrameRef.current = null;
      // URL 組み立てと replaceState は canvas-view-url-sync.ts へ切り出している
      // （replaceState の第 1 引数を null に保つ契約を co-located テストで固定するため）。
      replaceCanvasViewInUrl(latestViewForUrlRef.current, {
        width: window.innerWidth,
        height: window.innerHeight,
      });
    });
    return undefined;
  }, [readOnly, view]);

  // キャンバスや参照専用状態の切替時に、前の対象用の URL 同期を残さない。
  useEffect(() => {
    return () => {
      if (viewUrlSyncFrameRef.current !== null) {
        window.cancelAnimationFrame(viewUrlSyncFrameRef.current);
        viewUrlSyncFrameRef.current = null;
      }
    };
  }, [canvasId, readOnly]);

  // アンマウント時に will-change 監視タイマーを確実に破棄する（view effect 側はタイマーを
  // 跨いで維持するため cleanup を持たない。破棄はこのマウント専用 effect に集約する）。
  useEffect(() => {
    return () => {
      if (interactionTimerRef.current) {
        clearTimeout(interactionTimerRef.current);
        interactionTimerRef.current = null;
      }
    };
  }, []);

  // 文字・図形のにじみ（要望③）対策。
  // stage は `transform: scale(zoom)` で拡大する。will-change: transform を恒久指定すると、
  // ブラウザはレイヤを 1 度だけラスタライズしてビットマップを GPU 拡大するため、拡大時に
  // 文字や図形（DOM / SVG なのに）がぼやけて見える。そこで、view（pan/zoom）が変化している
  // 間だけ will-change: transform を有効化して合成を滑らかにし、操作が止まったら will-change
  // を解除する。解除によりブラウザが現在のズーム倍率で再ラスタライズし、ベクター本来の
  // 鮮明さで表示される（ZOOM_MAX=16 まで拡大できるため効果が大きい）。
  //
  // 性能配慮: キーボードパン等で view は毎フレーム更新されるため、操作中は state もタイマーも
  // 再設定せず（最後の操作時刻 lastViewChangeAtRef だけ更新する）、単一の監視タイマーが
  // 「最後の操作から VIEW_INTERACTION_IDLE_MS 経過」を自己再スケジュールで検知する。これにより
  // 毎フレームの clearTimeout/setTimeout を避ける。タイマーが消えている場合（StrictMode の
  // 再マウント等）は再武装する。
  useEffect(() => {
    lastViewChangeAtRef.current = Date.now();
    if (interactingRef.current && interactionTimerRef.current !== null) return;
    interactingRef.current = true;
    setIsViewInteracting(true);
    const watchdog = (): void => {
      const idle = Date.now() - lastViewChangeAtRef.current;
      if (idle >= VIEW_INTERACTION_IDLE_MS) {
        interactionTimerRef.current = null;
        interactingRef.current = false;
        setIsViewInteracting(false);
      } else {
        interactionTimerRef.current = setTimeout(watchdog, VIEW_INTERACTION_IDLE_MS - idle);
      }
    };
    if (interactionTimerRef.current) clearTimeout(interactionTimerRef.current);
    interactionTimerRef.current = setTimeout(watchdog, VIEW_INTERACTION_IDLE_MS);
  }, [view]);

  // ペン描画中: 増分点を他参加者へ一時 op として中継する（ゴースト表示用）。
  const onStrokeProgress = useCallback(
    ({
      strokeId,
      points,
      strokePattern,
    }: {
      strokeId: string;
      points: readonly StrokePoint[];
      strokePattern: StrokePattern;
    }) => {
      dispatcher.previewPenAppend(strokeId, points, strokePattern);
    },
    [dispatcher]
  );

  // ペンストローク確定時: 描画範囲を AABB に丸めて pen.stroke.commit op を送信。
  // 描画中の transient と同じ strokeId を確定オブジェクト ID に採用し、確定時に該当
  // ストロークのゴーストだけを正確に破棄できるようにする。
  const onStrokeCommit = useCallback(
    (params: {
      strokeId: string;
      points: readonly StrokePoint[];
      color: string;
      thickness: number;
      strokePattern: StrokePattern;
    }) => {
      const clippedPoints = params.points.map((point) => clampPointToCanvas(point));
      const aabb = computeAabb(clippedPoints, params.thickness);
      dispatcher.commitPenStroke(
        clippedPoints,
        params.color,
        params.thickness,
        aabb,
        params.strokeId,
        params.strokePattern
      );
    },
    [dispatcher]
  );

  // 付箋 / 図形 / テキストのクリック配置。ツールバーからのクリック配置・空白部のダブルクリック・
  // 空白部の右クリックメニューの「図形を配置」が、いずれもこの 1 つの関数を通るため、
  // 経路によって配置結果（位置・サイズ・スタイル・選択・編集モード）が食い違わない。
  // サイズは種類ごとに記憶した最後の配置・リサイズのサイズ（無ければ既定サイズ）を、
  // resolvePlacementSize の 1 か所で決める。図形は種類（丸・四角・矢印）ごとに記憶する。
  // 種別ごとの差（テキストだけ左上原点、付箋とテキストだけ編集モードへ入る、スタンプだけ選択しない、
  // セクションの既定タイトル、表の既定セル）は共通化せず分岐として残す。
  // 当初仕様（キャンバス外にも配置可）へ戻したため、キャンバス範囲外でも配置を許可する
  // （グリッド吸着は維持）。shapeKind を省くとツールバーで選択中の図形の種類を使う。戻り値は作成できたか。
  const placeObjectOfKind = useCallback(
    (
      kind: PlaceableObjectKind,
      stagePoint: { x: number; y: number },
      options?: { readonly shapeKind?: ShapeKind | undefined }
    ): boolean => {
      const placedShapeKind = options?.shapeKind ?? shapeKind;
      const sizeKey = placementSizeKeyOf(kind, placedShapeKind);
      // zoom は再レンダーを増やさないよう依存配列へ入れず、同ファイルの他の箇所と同じく getState() で都度読む。
      const size = resolvePlacementSize(sizeKey, loadPlacementPreference().sizes[sizeKey], {
        textFontSize,
        zoom: useCanvasStore.getState().view.zoom,
      });
      // 記憶したサイズで置いたときだけ、実際に置いた大きさで記憶し直す。テキストの記憶に高さが
      // 無い（幅だけを手で変えた）場合は、高さを記憶しないまま保つ。
      const memory: PlacementMemory = {
        kind,
        shapeKind: placedShapeKind,
        size: size.remembered
          ? { key: sizeKey, withHeight: kind !== "text" || size.rememberedHeight }
          : undefined,
      };
      let created: CanvasObject | null = null;
      switch (kind) {
        case "sticky": {
          const p = snapPointIfGridEnabled({
            x: stagePoint.x - size.w / 2,
            y: stagePoint.y - size.h / 2,
          });
          const frame = clampFrameToCanvas({
            x: p.x,
            y: p.y,
            w: size.w,
            h: size.h,
          });
          created = createAndRememberPlacement(
            dispatcher,
            {
              type: "sticky",
              ...frame,
              text: "",
              // stickyColor は hex 文字列をそのまま保持する（pen と同じ方針）。
              color: stickyColor,
              // 挿入前に選択した文字寄せを初期値として保持する（既定は左寄せ）。
              textAlign,
            },
            memory
          );
          // テキストと同様、配置直後に選択＋編集モードへ入れてすぐ文字を入力できるようにする
          // （従来はダブルクリックしないと入力できなかった）。付箋は空のまま確定しても
          // 本体（色付きの紙）が見えるため、テキストのような 0 文字自動削除は行わない。
          selectCreatedObject(created, useCanvasStore.getState().setSelectedObjectIds);
          if (created) {
            useCanvasStore.getState().setAutoEditObjectId(created.id);
          }
          break;
        }
        case "text": {
          const p = snapPointIfGridEnabled(stagePoint);
          // 既定の高さは文字サイズに応じて概算で広げ、極端な大型サイズで初期描画領域が
          // 不足して読みづらくならないようにする（最低でも 1 行分は確保する。textInitialHeight）。
          const frame = clampFrameToCanvas({
            x: p.x,
            y: p.y,
            w: size.w,
            h: size.h,
          });
          // テキストは「テキスト」等の初期文字を入れず、空のまま作成する。作成直後に選択＋編集モードへ
          // 入れ、すぐ入力できる状態にする（0 文字のまま確定した場合は TextObject 側で自動削除する）。
          created = createAndRememberPlacement(
            dispatcher,
            {
              type: "text",
              ...frame,
              text: "",
              // 配置前パネルで選んだ 5 項目（文字色・背景色・文字寄せ・文字サイズ・太字）を
              // そのまま初期書式として持たせる（未設定時は現行の既定外観になる）。
              ...resolveTextCreateStyle({
                color: textColor,
                fill: textFill,
                fontSize: textFontSize,
                bold: textBold,
                textAlign,
              }),
              // 記憶から復元した高さは利用者が選んだ高さなので、編集開始時の自動縮小で縮めない
              // （本文が収まらないときの拡大は従来どおり行う）。
              ...(size.rememberedHeight ? { manualHeight: true } : {}),
            },
            memory
          );
          selectCreatedObject(created, useCanvasStore.getState().setSelectedObjectIds);
          if (created) {
            useCanvasStore.getState().setAutoEditObjectId(created.id);
          }
          break;
        }
        case "section": {
          // セクションは大型の領域コンテナのため、クリック位置を中心に配置する。
          const p = snapPointIfGridEnabled({
            x: stagePoint.x - size.w / 2,
            y: stagePoint.y - size.h / 2,
          });
          const frame = clampFrameToCanvas({
            x: p.x,
            y: p.y,
            w: size.w,
            h: size.h,
          });
          created = createAndRememberPlacement(
            dispatcher,
            {
              type: "section",
              ...frame,
              text: SECTION_DEFAULT_TITLE,
              fill: sectionFill,
            },
            memory
          );
          selectCreatedObject(created, useCanvasStore.getState().setSelectedObjectIds);
          break;
        }
        case "shape": {
          const p = snapPointIfGridEnabled({
            x: stagePoint.x - size.w / 2,
            y: stagePoint.y - size.h / 2,
          });
          const frame = clampFrameToCanvas({
            x: p.x,
            y: p.y,
            w: size.w,
            h: size.h,
          });
          created = createAndRememberPlacement(
            dispatcher,
            {
              type: "shape",
              shapeKind: placedShapeKind,
              ...frame,
              ...resolveShapeCreateStyle({
                shapeKind: placedShapeKind,
                fill: shapeFill,
                stroke: shapeStroke,
                strokePattern: shapeStrokePattern,
                strokeWidth: shapeStrokeWidth,
              }),
              // UI からクリック配置する矢印も軸そろえ表現へ統一する（向きはサイズの記憶と分け、
              // 記憶した大きさで置くときも従来どおり左辺起点にする）。
              ...(placedShapeKind === "arrow" ? { arrowFromEdge: "left" as const } : {}),
            },
            memory
          );
          selectCreatedObject(created, useCanvasStore.getState().setSelectedObjectIds);
          break;
        }
        case "table": {
          const p = snapPointIfGridEnabled({
            x: stagePoint.x - size.w / 2,
            y: stagePoint.y - size.h / 2,
          });
          const frame = clampFrameToCanvas({
            x: p.x,
            y: p.y,
            w: size.w,
            h: size.h,
          });
          created = createAndRememberPlacement(
            dispatcher,
            {
              type: "table",
              ...frame,
              cells: createDefaultTableCells(),
            },
            memory
          );
          selectCreatedObject(created, useCanvasStore.getState().setSelectedObjectIds);
          break;
        }
        case "stamp": {
          // スタンプは記憶したサイズが無ければ、そのときの拡大率から初期サイズを決める（画面上の
          // 見かけをおおよそ一定〈STAMP_TARGET_SCREEN_PX〉に保つ）。手でリサイズした大きさは
          // ステージ座標のまま記憶し、次の配置ではその大きさを拡大率によらず使う（絵柄によらず共通）。
          const p = snapPointIfGridEnabled({
            x: stagePoint.x - size.w / 2,
            y: stagePoint.y - size.h / 2,
          });
          const frame = clampFrameToCanvas({
            x: p.x,
            y: p.y,
            w: size.w,
            h: size.h,
          });
          // アバタースタンプは配置時点の利用者アバターを「自己完結スナップショット」として保存する。
          // 可変・失効し得るリモート https 参照を避けるため、data:image/ スナップショット
          // （usePortableAvatarImageUrl が idp-file: 等を縮小 data URL 化した値）のときだけ avatarSrc を
          // 載せる。検証対象と永続化対象を一致させるため、実際に保存する sanitize 後の値そのものを
          // isSnapshotAvatarImageUrl で検証する（検証は raw・保存は sanitize 後、というズレでサーバ 400 に
          // ならないようにする）。スナップショット不可（リモート https・未設定）のときは avatar スタンプを
          // 作らず、既定の絵文字スタンプへフォールバックする。
          const avatarSnapshot =
            stampKind === "avatar"
              ? sanitizeAvatarImageUrl(currentActor.avatarImageUrl)
              : undefined;
          const avatarSrc = isSnapshotAvatarImageUrl(avatarSnapshot) ? avatarSnapshot : undefined;
          const effectiveStampKind =
            stampKind === "avatar" && avatarSrc === undefined ? "thumbs-up" : stampKind;
          // スタンプだけは配置後に選択もしない（従来の挙動をそのまま保つ）。
          created = createAndRememberPlacement(
            dispatcher,
            {
              type: "stamp",
              stampKind: effectiveStampKind,
              ...frame,
              ...(avatarSrc !== undefined ? { avatarSrc } : {}),
            },
            memory
          );
          break;
        }
        default: {
          // 種別を増やしたときに、ここで型エラーとして気づけるようにする（無音で「何も置かない」に
          // ならないようにするため）。
          const exhaustiveCheck: never = kind;
          void exhaustiveCheck;
          break;
        }
      }
      // 「前回置いた種別」とサイズの記憶は、実際に作成できたときだけ createAndRememberPlacement が更新する。
      return created !== null;
    },
    [
      currentActor,
      dispatcher,
      sectionFill,
      shapeFill,
      shapeKind,
      shapeStroke,
      shapeStrokePattern,
      shapeStrokeWidth,
      stampKind,
      stickyColor,
      textAlign,
      textBold,
      textColor,
      textFill,
      textFontSize,
    ]
  );

  // 選択中の配置ツールに応じた配置。実体は placeObjectOfKind にあり、ここは種別への読み替えだけを行う。
  // コメントは図形ではなく入力ウィンドウを開く操作のため、ここに残す（「前回置いた種別」も更新しない）。
  const onPlaceObject = useCallback(
    ({ stagePoint }: { stagePoint: { x: number; y: number } }) => {
      switch (tool) {
        case "place-sticky":
          placeObjectOfKind("sticky", stagePoint);
          return;
        case "place-text":
          placeObjectOfKind("text", stagePoint);
          return;
        case "place-section":
          placeObjectOfKind("section", stagePoint);
          return;
        case "place-shape":
          placeObjectOfKind("shape", stagePoint);
          return;
        case "place-table":
          placeObjectOfKind("table", stagePoint);
          return;
        case "place-stamp":
          placeObjectOfKind("stamp", stagePoint);
          return;
        case "place-comment": {
          // コメントは即作成せず、クリック位置（アンカー）に入力ウィンドウを開く。
          // 実際のスレッド作成は入力確定（ENTER）時に CommentLayer 側で行う。
          useCanvasStore.getState().setCommentDraft({ x: stagePoint.x, y: stagePoint.y });
          return;
        }
        default:
          return;
      }
    },
    [placeObjectOfKind, tool]
  );

  // 図形をドラッグで任意サイズ配置したときの確定。frame は use-pointer-mode 側で
  // グリッド吸着・最小サイズ・サイズの非負化を反映済み。矢印のときだけ起点角・
  // 起点辺を載せ、丸 / 四角では向きを使わない。
  // ドラッグ配置も利用者が「置いた」操作なので、クリック配置と同じく種別を記録する
  // （記録しないと、ドラッグで置いた直後のダブルクリックがそれより前の種別を置いてしまう）。
  // サイズはその図形の種類の記憶として、置いた大きさで記憶する（向きはサイズと分けて記憶しない）。
  const onPlaceShapeDrag = useCallback(
    ({ frame }: { frame: ShapeDragFrame }) => {
      const created = createAndRememberPlacement(
        dispatcher,
        {
          type: "shape",
          shapeKind,
          x: frame.x,
          y: frame.y,
          w: frame.w,
          h: frame.h,
          ...resolveShapeCreateStyle({
            shapeKind,
            fill: shapeFill,
            stroke: shapeStroke,
            strokePattern: shapeStrokePattern,
            strokeWidth: shapeStrokeWidth,
          }),
          ...(shapeKind === "arrow" ? resolveArrowOriginFields(frame) : {}),
        },
        {
          kind: "shape",
          shapeKind,
          size: { key: placementSizeKeyOf("shape", shapeKind), withHeight: true },
        }
      );
      selectCreatedObject(created, useCanvasStore.getState().setSelectedObjectIds);
    },
    [dispatcher, shapeFill, shapeKind, shapeStroke, shapeStrokePattern, shapeStrokeWidth]
  );

  // セクションをドラッグで任意サイズ配置したときの確定。frame は use-pointer-mode 側で
  // グリッド吸着・セクション固有の最小サイズ（200×120）・キャンバス範囲内 clamp を反映済み。
  const onPlaceSectionDrag = useCallback(
    ({ frame }: { frame: ShapeDragFrame }) => {
      const created = createAndRememberPlacement(
        dispatcher,
        {
          type: "section",
          x: frame.x,
          y: frame.y,
          w: frame.w,
          h: frame.h,
          text: SECTION_DEFAULT_TITLE,
          fill: sectionFill,
        },
        { kind: "section", size: { key: "section", withHeight: true } }
      );
      selectCreatedObject(created, useCanvasStore.getState().setSelectedObjectIds);
    },
    [dispatcher, sectionFill]
  );

  // 表をドラッグで配置する。セルはクリック配置と同じ空の 3×3 とし、編集モードには入らない。
  const onPlaceTableDrag = useCallback(
    ({ frame }: { frame: ShapeDragFrame }) => {
      const created = createAndRememberPlacement(
        dispatcher,
        {
          type: "table",
          x: frame.x,
          y: frame.y,
          w: frame.w,
          h: frame.h,
          cells: createDefaultTableCells(),
        },
        { kind: "table", size: { key: "table", withHeight: true } }
      );
      selectCreatedObject(created, useCanvasStore.getState().setSelectedObjectIds);
    },
    [dispatcher]
  );

  // 付箋をドラッグで任意サイズ配置したときの確定。frame は use-pointer-mode 側でグリッド吸着・
  // 最小サイズ（16px）・上限を反映済み。配置後の選択・編集モードはクリック配置と同じにする。
  const onPlaceStickyDrag = useCallback(
    ({ frame }: { frame: ShapeDragFrame }) => {
      const created = createAndRememberPlacement(
        dispatcher,
        {
          type: "sticky",
          x: frame.x,
          y: frame.y,
          w: frame.w,
          h: frame.h,
          text: "",
          color: stickyColor,
          textAlign,
        },
        { kind: "sticky", size: { key: "sticky", withHeight: true } }
      );
      selectCreatedObject(created, useCanvasStore.getState().setSelectedObjectIds);
      if (created) useCanvasStore.getState().setAutoEditObjectId(created.id);
    },
    [dispatcher, stickyColor, textAlign]
  );

  // テキストをドラッグで任意サイズ配置したときの確定。ドラッグで選んだ高さは利用者が明示した高さなので
  // manualHeight を付け、編集開始時の自動縮小で縮めない（本文が収まらないときの拡大は従来どおり）。
  // 配置後の選択・編集モードはクリック配置と同じにする。
  const onPlaceTextDrag = useCallback(
    ({ frame }: { frame: ShapeDragFrame }) => {
      const created = createAndRememberPlacement(
        dispatcher,
        {
          type: "text",
          x: frame.x,
          y: frame.y,
          w: frame.w,
          h: frame.h,
          text: "",
          ...resolveTextCreateStyle({
            color: textColor,
            fill: textFill,
            fontSize: textFontSize,
            bold: textBold,
            textAlign,
          }),
          manualHeight: true,
        },
        { kind: "text", size: { key: "text", withHeight: true } }
      );
      selectCreatedObject(created, useCanvasStore.getState().setSelectedObjectIds);
      if (created) useCanvasStore.getState().setAutoEditObjectId(created.id);
    },
    [dispatcher, textAlign, textBold, textColor, textFill, textFontSize]
  );

  // クリップボード画像: presigned PUT でアップロードしてから image オブジェクトを作成
  const onPasteImage = useCallback(
    (file: File, position: { x: number; y: number }) => {
      void uploadAndPlaceImage(canvasApi, canvasId, dispatcher, file, position, showToast);
    },
    [canvasApi, canvasId, dispatcher, showToast]
  );

  // クリップボード URL: OGP 取得してリンクカード化
  const onPasteUrl = useCallback(
    (url: string, position: { x: number; y: number }) => {
      void (async () => {
        const p = snapPointIfGridEnabled(position);
        try {
          const og = await canvasApi.fetchOgp(url);
          const frame = clampFrameToCanvas({ x: p.x, y: p.y, w: 320, h: 200 });
          dispatcher.createObject({
            type: "link-card",
            ...frame,
            url,
            title: og.title ?? url,
            description: og.description,
            imageUrl: og.image,
            siteName: og.siteName,
          });
        } catch {
          // OGP 取得失敗時は URL テキストとして配置
          const frame = clampFrameToCanvas({
            x: p.x,
            y: p.y,
            w: TEXT_DEFAULT_W,
            h: TEXT_DEFAULT_H,
          });
          dispatcher.createObject({
            type: "text",
            ...frame,
            text: url,
            color: "#1e63d4",
          });
        }
      })();
    },
    [canvasApi, dispatcher, showToast]
  );

  // クリップボード URL: 付箋・テキスト図形を選択していれば、その本文を URL のリンクにする。
  // 対象が無ければ false を返し、従来どおり OGP リンクカードの作成へ進ませる。
  const onPasteUrlIntoSelectedTexts = useCallback(
    (url: string): boolean => {
      const selectedObjectIds = useCanvasStore.getState().selectedObjectIds;
      const objects = useCanvasDocStore.getState().objects;
      const plan = planLinkifySelectedObjects({ objects, selectedObjectIds, url });
      if (plan.candidateCount === 0) return false;
      if (plan.updates.length > 0) dispatcher.updateTexts(plan.updates);
      if (plan.skippedTooLong > 0) {
        showToast(
          "warning",
          plan.updates.length > 0
            ? `${plan.skippedTooLong} 件は本文が長すぎるためリンクにできませんでした。`
            : "本文が長すぎるためリンクにできませんでした。"
        );
      }
      return true;
    },
    [dispatcher, showToast]
  );

  // キャンバス固有形式・画像・URL のどれにも当たらない文字列を、現在の配置書式で作成する。
  const onPasteText = useCallback(
    (text: string, position: { x: number; y: number }) => {
      const normalizedText = text.trim();
      if (normalizedText.length === 0) return;
      const p = snapPointIfGridEnabled(position);
      const style = resolveTextCreateStyle({
        color: textColor,
        fill: textFill,
        fontSize: textFontSize,
        bold: textBold,
        textAlign,
      });
      const provisionalDraft = {
        type: "text",
        ...clampFrameToCanvas({
          x: p.x,
          y: p.y,
          w: TEXT_DEFAULT_W,
          h: textInitialHeight(textFontSize),
        }),
        text: "",
        ...style,
      } satisfies CanvasObjectDraft;
      const envelopeBytes = dispatcher.measureCreateEnvelopeBytes(provisionalDraft);
      const fit = fitPastedTextForBudget({
        text: normalizedText,
        budgetBytes: CLIENT_MESSAGE_MAX_BYTES - envelopeBytes - PASTE_TEXT_CREATE_MARGIN_BYTES,
      });
      if (fit.text.length === 0) return;

      // 保存する本文を確定してから寸法を求め、表示と送信内容の基準を一致させる。
      const size = resolvePastedTextFrameSize({ text: fit.text, fontPx: textFontSize });
      const created = dispatcher.createObject({
        ...provisionalDraft,
        ...clampFrameToCanvas({ x: p.x, y: p.y, ...size }),
        text: fit.text,
      });
      selectCreatedObject(created, useCanvasStore.getState().setSelectedObjectIds);
      if (!created) return;
      const notice = textBodyFitNotice({ ...fit, shouldSend: true }, TEXT_BODY_MAX_LENGTH);
      if (notice) showToast(notice.level, notice.message);
    },
    [dispatcher, showToast, textAlign, textBold, textColor, textFill, textFontSize]
  );

  const onDropImage = useCallback(
    (file: File, position: { x: number; y: number }) => {
      void uploadAndPlaceImage(canvasApi, canvasId, dispatcher, file, position, showToast);
    },
    [canvasApi, canvasId, dispatcher, showToast]
  );

  const onPasteCanvasObjects = useCallback(
    (objects: readonly CanvasObject[], position: { x: number; y: number }) => {
      pasteCanvasObjects(objects, position, dispatcher, showToast);
    },
    [dispatcher, showToast]
  );

  // GFM 表は既定書式の表図形として、貼り付け位置へ中心合わせして配置する。
  const onPasteTable = useCallback(
    (
      cells: readonly (readonly string[])[],
      parserTruncated: boolean,
      position: { x: number; y: number }
    ) => {
      const fitted = fitPastedTableCells(cells);
      if (fitted === null) {
        showToast("warning", "表の内容が大きすぎるため貼り付けできませんでした。");
        return;
      }
      const size = resolvePastedTableFrameSize({ cells: fitted.cells, fontPx: 16 });
      const topLeft = snapPointIfGridEnabled({
        x: position.x - size.w / 2,
        y: position.y - size.h / 2,
      });
      const created = dispatcher.createObject({
        type: "table",
        ...clampFrameToCanvas({ ...topLeft, ...size }),
        cells: fitted.cells,
      });
      selectCreatedObject(created, useCanvasStore.getState().setSelectedObjectIds);
      if (parserTruncated || fitted.truncated) {
        showToast("warning", "表の一部を切り詰めて貼り付けました。");
      }
    },
    [dispatcher, showToast]
  );

  // 空白部の右クリックメニュー「貼り付け」からの貼り付け実行。Ctrl+V と同じ振り分けを
  // 非同期 Clipboard API 経由で行い、貼り付け先は右クリック位置のステージ座標を使う。
  const onEmptyAreaPaste = useCallback(
    (position: { x: number; y: number }) => {
      void performClipboardPaste(
        { onPasteImage, onPasteUrl, onPasteCanvasObjects, onPasteTable, onPasteText },
        position
      ).then((result) => {
        if (result === "empty") {
          showToast("info", "貼り付けるものがありません。");
        } else if (result === "unavailable") {
          showToast(
            "warning",
            "このブラウザではメニューからの貼り付けに対応していません。Ctrl+V をお試しください。"
          );
        }
      });
    },
    [onPasteImage, onPasteUrl, onPasteCanvasObjects, onPasteTable, onPasteText, showToast]
  );

  // 前回置いた種別（図形は前回の種類。未記録ならツールバーで選択中の種類）を、その種類の記憶した
  // サイズで置く。空白部のダブルクリックと右クリックメニューの「図形を配置」が共有する。
  const placeLastPlacedObject = useCallback(
    (position: { x: number; y: number }) => {
      const preference = loadPlacementPreference();
      placeObjectOfKind(preference.lastPlacedKind, position, {
        shapeKind: preference.lastShapeKind,
      });
    },
    [placeObjectOfKind]
  );

  // 空白部の右クリックメニュー「図形を配置」からの配置。位置は右クリックした瞬間のステージ座標。
  const onEmptyAreaPlaceLastObject = useCallback(
    (position: { x: number; y: number }) => {
      placeLastPlacedObject(position);
    },
    [placeLastPlacedObject]
  );

  const onDropVideo = useCallback(
    (file: File, position: { x: number; y: number }) => {
      void uploadAndPlaceVideo(canvasApi, canvasId, dispatcher, file, position, showToast);
    },
    [canvasApi, canvasId, dispatcher, showToast]
  );

  // 入力ハンドラを束ねる。参照専用（埋め込み）ではパン / ズーム（useKeyboardPan / useWheelPanZoom /
  // useTouchPinchZoom）だけを残し、編集系（ショートカット・ドロップ・貼り付け・配置/描画・範囲選択・
  // カーソル配信）を無効化する。
  useGlobalShortcuts(dispatcher, { readOnly });
  useKeyboardPan();
  useWheelPanZoom(containerRef);
  // タッチ端末の 2 本指ピンチによる拡大縮小 + 移動。参照専用（埋め込み）でも拡大縮小は必要なため
  // 有効にする（1 本指のパンとは競合しない）。
  useTouchPinchZoom(containerRef);
  // パン中フラグを文書ルートの属性として反映する（コメントレイヤー等の兄弟 UI のカーソル固定に使う）。
  useCanvasPanningDocumentAttribute(containerRef);
  useDropHandler(containerRef, { onDropImage, onDropVideo, readOnly });
  usePasteHandler({
    onPasteCanvasObjects,
    onPasteImage,
    onPasteTable,
    onPasteUrl,
    onPasteUrlIntoSelectedTexts,
    onPasteText,
    readOnly,
  });
  usePointerMode(
    { stageRef: containerRef, penLayerRef, readOnly },
    {
      onStrokeCommit,
      onStrokeProgress,
      onPlaceObject,
      onPlaceStickyDrag,
      onPlaceTextDrag,
      onPlaceShapeDrag,
      onPlaceSectionDrag,
      onPlaceTableDrag,
    }
  );
  // 右ボタンドラッグでの範囲指定選択。左ボタン系（パン/ドラッグ/描画/配置）と排他。
  useMarqueeSelection(containerRef, { readOnly });
  useCursorBroadcaster({ readOnly });

  // 空白部の右クリックでコンテキストメニューを開く。
  // オブジェクト上の右クリックは canvas-objects-layer が capture 段階で stopPropagation するため
  // ここには来ない。右ドラッグパン / マーキー直後の contextmenu は window capture で抑止済みのため、
  // 「空白部の単なる右クリック」だけがここへ届く。参照専用ではブラウザ標準に委ねる。
  // ただし contextmenu を押下の時点で発火するブラウザ（Linux の Chromium 等）では、その抑止が
  // 間に合わず、右ドラッグの開始直後にもここへ届く。そのためメニュー側で素通しにできるよう、
  // 押下が続いているか（buttons）を一緒に渡す。
  const onContainerContextMenu = (event: ReactMouseEvent<HTMLDivElement>): void => {
    if (readOnly) return;
    event.preventDefault();
    const v = useCanvasStore.getState().view;
    const s = screenToStage({ x: event.clientX, y: event.clientY }, v);
    useCanvasStore.getState().openEmptyContextMenu({
      clientX: event.clientX,
      clientY: event.clientY,
      stageX: s.x,
      stageY: s.y,
      // ボタンを押したままの contextmenu（Linux の Chromium 等）では、開いたメニューが
      // 進行中の右ボタンドラッグを奪わないよう、離されるまで素通しにする。
      pointerHeld: event.buttons !== 0,
    });
  };

  // ダブルクリックを構成する 2 回の押下が、それぞれ「ダブルクリック配置をしてよい状態」から
  // 始まったかを控える。dblclick が届いた時点の状態では判定できないため（下の 2 点）、
  // 押下の時点で見る必要がある。
  //  - 配置ツールは 1 回目の配置で自ら setTool("idle") を呼ぶ（use-pointer-mode）ため、
  //    dblclick の時点では常に idle に見える
  //  - コメントの下書きは、外側の押下が document まで届いた時点で取り消される（comment-layer）ため、
  //    dblclick の時点では常に閉じて見える
  // 直近 2 回ぶんを持つのは、ダブルクリックが「直前 2 回の主ボタン押下」で構成されるためで、
  // クリック連鎖の回数（detail）や時間のしきい値に依存せずに済む。
  const placeableAtPreviousPointerDownRef = useRef(true);
  const placeableAtLatestPointerDownRef = useRef(true);

  // 押下の記録とダブルクリック配置は、React の合成イベント（onPointerDownCapture / onDoubleClick）
  // ではなくコンテナへのネイティブリスナーで登録する。理由は 2 つある。
  //  - capture 段階で控える必要がある。use-pointer-mode はコンテナの bubble 段階で pointerdown を
  //    受け、スタンプ / コメントはその場で配置して idle へ戻すため（付箋 / テキスト / 図形などは
  //    pointerup で配置して idle へ戻す）、bubble で読むと「最初から idle だった」と区別できなくなる
  //  - 同じコンテナの入力処理（use-pointer-mode / use-marquee-selection / use-drop-handler）は
  //    すべてネイティブリスナーで登録されており、そこへ React のルート委譲経由の配送を混ぜない
  // どちらのリスナーも、配置しないときは stopPropagation も preventDefault も行わない。
  const placeLastPlacedObjectRef = useRef(placeLastPlacedObject);
  placeLastPlacedObjectRef.current = placeLastPlacedObject;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const onPointerDownCapture = (event: PointerEvent): void => {
      // ダブルクリックを構成するのは主ボタンの押下だけ（右ドラッグの範囲選択・中ボタンのパンは対象外）。
      if (event.button !== 0) return;
      const state = useCanvasStore.getState();
      placeableAtPreviousPointerDownRef.current = placeableAtLatestPointerDownRef.current;
      placeableAtLatestPointerDownRef.current =
        state.tool === "idle" && state.commentDraft === null;
    };

    // 空白部のダブルクリックで、前回置いた種別の図形を新しく配置する。
    // capture 段階で止めると図形側のダブルクリック（編集モード）が動かなくなるため bubble で受け、
    // 「イベントの発生元がコンテナ自身か」だけで空白部を判定する
    // （ステージ・グリッド・PDF 出力範囲の点線と注釈はいずれも pointer-events: none で、発生元にならない）。
    const onDoubleClick = (event: MouseEvent): void => {
      if (readOnly) return;
      // 配置ツールで置いた直後の 2 回目のクリック（二重配置になる）と、コメントの下書き中
      // （取り消し操作と重なる）では何もしない。
      if (!placeableAtPreviousPointerDownRef.current || !placeableAtLatestPointerDownRef.current) {
        return;
      }
      // 図形・選択ハンドル・コメントピンの上なら、それぞれの既存処理に任せる。
      if (event.target !== container) return;
      event.preventDefault();
      const v = useCanvasStore.getState().view;
      const stagePoint = screenToStage({ x: event.clientX, y: event.clientY }, v);
      placeLastPlacedObjectRef.current(stagePoint);
    };

    container.addEventListener("pointerdown", onPointerDownCapture, { capture: true });
    container.addEventListener("dblclick", onDoubleClick);
    return () => {
      container.removeEventListener("pointerdown", onPointerDownCapture, { capture: true });
      container.removeEventListener("dblclick", onDoubleClick);
    };
  }, [readOnly]);

  return (
    <>
      <div
        ref={containerRef}
        data-testid="canvas-container"
        onContextMenu={onContainerContextMenu}
        className={
          tool === "panning"
            ? "canvas-cursor-grabbing"
            : tool === "draw-pen"
              ? "canvas-cursor-crosshair"
              : tool === "place-sticky" ||
                  tool === "place-section" ||
                  tool === "place-shape" ||
                  tool === "place-table" ||
                  tool === "place-text" ||
                  tool === "place-stamp" ||
                  tool === "place-comment"
                ? "canvas-cursor-crosshair"
                : "canvas-cursor-default"
        }
        style={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          touchAction: "none",
        }}
      >
        {/* 画面全体の白い下地とグリッド点、PDF 出力範囲の点線と注釈。いずれも画面座標の層で、
            ステージ（図形・選択）やコメントの層より背面に置き、pointer-events を持たない。 */}
        <CanvasGrid />
        <PdfBoundaryOverlay />
        <div
          data-testid="canvas-stage"
          style={
            {
              position: "absolute",
              width: `${CANVAS_WIDTH_PX}px`,
              height: `${CANVAS_HEIGHT_PX}px`,
              transformOrigin: "0 0",
              transform: `translate3d(${view.panX}px, ${view.panY}px, 0) scale(${view.zoom})`,
              // パン/ズーム操作中のみ合成レイヤへ昇格。静止時は auto に戻して再ラスタライズさせ、
              // 拡大時の文字・図形のにじみを防ぐ（要望③）。
              willChange: isViewInteracting ? "transform" : "auto",
              // 白い下地・グリッドは CanvasGrid が画面全体へ、PDF 出力範囲の境界は PdfBoundaryOverlay が描く。
              // この div はステージ座標の原点（左上 = 0,0）を定める座標コンテナに徹し、面の見た目は持たない
              // （図形はこの原点基準で配置され、キャンバス矩形の外側にも描画できる）。
              pointerEvents: "none",
              // 選択 UI（各オブジェクトの選択アウトライン等）を、このステージの scale(zoom) に
              // 依らず画面上で一定の太さに描くための逆スケール係数（= 1/zoom）。子孫は
              // calc(<px> * var(--canvas-inv-zoom)) でズーム非依存の線幅を表現でき、zoom 変化のたびに
              // 各オブジェクトを再レンダーせずとも CSS カスケードだけで太さが追従する。
              "--canvas-inv-zoom": 1 / view.zoom,
            } as CSSProperties
          }
        >
          <CanvasObjectsLayer />
          <CanvasRemotePreviewsLayer currentUserId={currentUserId} />
          <CanvasRemoteSelectionsLayer currentUserId={currentUserId} />
          <CanvasRemoteCaretsLayer currentUserId={currentUserId} />
          <CanvasSelectionOverlay />
          <CanvasAlignmentGuidesLayer />
          {/* コネクタ作成（接続ドット→createConnector）は編集操作のため、参照専用では描画しない
            （選択経路は readOnly で塞いでいるが、防御として overlay 自体も出さない）。 */}
          {readOnly ? null : <CanvasConnectorOverlay stageContainerRef={containerRef} />}
          <CanvasMarqueeOverlay />
          <CanvasShapePlacementOverlay />
          <CanvasPenLayer handleRef={penLayerRef} />
          <CanvasCursorsLayer currentUserId={currentUserId} />
        </div>
      </div>
      {/* 空白部の右クリックで開く「貼り付け」「図形を配置」メニュー（参照専用では出さない）。
          fixed 配置なので描画ツリー上の位置は表示へ影響しない。 */}
      {readOnly ? null : (
        <EmptyAreaContextMenu
          onPaste={onEmptyAreaPaste}
          onPlaceLastObject={onEmptyAreaPlaceLastObject}
        />
      )}
    </>
  );
}

// 貼り付け複製で先に id を確定するためのジェネレータ（dispatcher と同じ文字種・長さ）。
// コネクタの端点 id を新しい複製先へ貼り替えるため、createObjects へ渡す前に id を決める必要がある。
const generatePastedObjectId = customAlphabet(
  "23456789abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ",
  16
);

function pasteCanvasObjects(
  objects: readonly CanvasObject[],
  position: { x: number; y: number },
  dispatcher: CanvasOpDispatcher,
  showToast: ShowToast
): void {
  if (objects.length === 0) return;
  // アップロード未完了の画像・動画（blob:/data: src）は貼り付け対象から除外する。
  // そのまま複製すると公開 URL を持たない object.create が再配信され、他参加者で
  // 解決できない画像や永続的な仮表示になるため（コピー側の除外と二重化）。
  // 混在ペイロードでは未完了添付だけを除外し、確定済みのオブジェクトは通常通り貼り付ける。
  const attachmentReady = objects.filter((object) => !isTransientAttachmentObject(object));
  // 未完了添付（画像・動画）を除外した件数。トーストはこの理由のときだけ出す。
  const attachmentExcludedCount = objects.length - attachmentReady.length;
  // コネクタ（接続矢印）は両端の図形が同じ貼り付け対象に含まれるときだけ複製する。
  // 端点 id を新しく複製した図形の id へ貼り替えるため、片方しか含まれない（＝既存図形や
  // 別キャンバスの図形を指す）コネクタは宙ぶらりんになるので除外する（端点未選択時の自然な挙動
  // のため、これ自体はトーストしない）。
  const attachmentReadyIds = new Set(attachmentReady.map((object) => object.id));
  const placeable = attachmentReady.filter(
    (object) =>
      object.type !== "connector" ||
      (attachmentReadyIds.has(object.from.objectId) && attachmentReadyIds.has(object.to.objectId))
  );
  if (placeable.length === 0) {
    // 貼り付けできるものが無い。理由が未完了添付なら案内し、コネクタのみ（端点未選択）なら静かに終える。
    if (attachmentExcludedCount > 0) {
      showToast(
        "warning",
        "アップロード完了前の画像・動画は貼り付けできません。完了後にもう一度お試しください。"
      );
    }
    return;
  }
  // 貼り付け対象全体の外接矩形を求め、マウス位置へ中心合わせで複製する。
  const bounds = placeable.reduce(
    (acc, object) => ({
      left: Math.min(acc.left, object.x),
      top: Math.min(acc.top, object.y),
      right: Math.max(acc.right, object.x + object.w),
      bottom: Math.max(acc.bottom, object.y + object.h),
    }),
    { left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity }
  );
  const boundsWidth = bounds.right - bounds.left;
  const boundsHeight = bounds.bottom - bounds.top;
  // 当初仕様（キャンバス外にも配置可）へ戻したため、貼り付け対象がキャンバスより大きくても作成を許可する。
  const rawOffsetX = position.x - (bounds.left + bounds.right) / 2;
  const rawOffsetY = position.y - (bounds.top + bounds.bottom) / 2;
  // グリッド吸着 ON のときは「貼り付け後の左上」を 10px グリッドに揃える。
  // 中央値ではなく左上で丸めることで複数オブジェクトの相対位置をそのまま保つ。
  const topLeft = snapPointIfGridEnabled({
    x: bounds.left + rawOffsetX,
    y: bounds.top + rawOffsetY,
  });
  let offsetX = topLeft.x - bounds.left;
  let offsetY = topLeft.y - bounds.top;
  const clampedBounds = clampFrameToCanvas({
    x: bounds.left + offsetX,
    y: bounds.top + offsetY,
    w: boundsWidth,
    h: boundsHeight,
  });
  offsetX += clampedBounds.x - (bounds.left + offsetX);
  offsetY += clampedBounds.y - (bounds.top + offsetY);
  // 旧 id → 複製後の新 id の対応表。コネクタの端点 id を貼り替えるため、createObjects へ
  // 渡す前に各オブジェクトの新 id を確定しておく。
  const idMap = new Map(placeable.map((object) => [object.id, generatePastedObjectId()]));
  // コネクタは端点図形より後に create する必要がある（サーバは create 時に端点の存在を検証し、
  // まだ作成されていない端点を参照するコネクタを reject するため）。端点 → コネクタの順に並べる。
  const endpointDrafts: CanvasObjectDraft[] = [];
  const connectorDrafts: CanvasObjectDraft[] = [];
  for (const object of placeable) {
    const stripped = cloneObjectAsDraft(object, { x: offsetX, y: offsetY });
    const newId = idMap.get(object.id)!;
    if (object.type === "connector") {
      // 端点 id を新しい複製先へ貼り替える（両端が placeable に含まれることは上で保証済み）。
      connectorDrafts.push({
        ...stripped,
        id: newId,
        x: object.x + offsetX,
        y: object.y + offsetY,
        from: { ...object.from, objectId: idMap.get(object.from.objectId)! },
        to: { ...object.to, objectId: idMap.get(object.to.objectId)! },
      } as CanvasObjectDraft);
      continue;
    }
    endpointDrafts.push({
      ...stripped,
      id: newId,
      x: object.x + offsetX,
      y: object.y + offsetY,
    });
  }
  const pastedIds = dispatcher
    .createObjects([...endpointDrafts, ...connectorDrafts])
    .map((object) => object.id);
  useCanvasStore.getState().setSelectedObjectIds(pastedIds);
  if (attachmentExcludedCount > 0) {
    showToast("info", "アップロード完了前の画像・動画は貼り付け対象から除外しました。");
  }
}

/**
 * ストローク座標群と太さから外接矩形を求める。pen-stroke オブジェクトの x,y,w,h に使う。
 */
function computeAabb(
  points: readonly { x: number; y: number }[],
  thickness: number
): { x: number; y: number; w: number; h: number } {
  if (points.length === 0) return { x: 0, y: 0, w: 0, h: 0 };
  const first = points[0];
  if (!first) return { x: 0, y: 0, w: 0, h: 0 };
  let minX = first.x;
  let minY = first.y;
  let maxX = first.x;
  let maxY = first.y;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  const pad = thickness;
  return {
    x: minX - pad,
    y: minY - pad,
    w: maxX - minX + pad * 2,
    h: maxY - minY + pad * 2,
  };
}

/**
 * 画像ファイルを「先に仮表示、あとから本体同期」で配置する。
 *
 * 1) 作成者にはローカル blob でフル画質を即表示しつつ、他参加者へは超圧縮 LQIP を src にした
 *    create を即配信する（誰かが画像を置いたことと、ぼやけた仮プレビューがすぐ伝わる）。
 *    blob: URL は他参加者が解決できないため、create op には絶対に載せない。
 * 2) presigned PUT で S3 にバイナリ送信
 * 3) /uploads:complete を叩き、CloudFront 公開 URL を取得（未設定なら null）
 * 4) publicUrl が取れたら attachment.update op で全員を公開 URL 表示へ収束させる
 *    publicUrl が null（dev/mock で共有不可）の場合は仮表示のまま据え置く
 *    アップロード失敗時は仮表示を全員から取り除き、トーストで通知する
 */
async function uploadAndPlaceImage(
  canvasApi: CanvasApiClient,
  canvasId: string,
  dispatcher: CanvasOpDispatcher,
  file: File,
  position: { x: number; y: number },
  showToast: ShowToast
): Promise<void> {
  // 楽観更新: まず元ファイルで仮表示（プレースホルダ）を即時作成する（AGENTS の楽観更新方針。
  // 大きい画像の自動縮小・変換に時間がかかっても「無反応」に見せない）。
  const placed = await placeImagePlaceholder(dispatcher, file, position);
  if (!placed) return;
  const { objectId, overrideOpId, localUrl } = placed;
  // アップロード前処理: 上限超過なら自動で縮小し、非対応形式でもデコード可能なら対応形式へ自動変換する。
  // デコード不能・縮小しても収まらない場合は、仮表示を取り消して原因の分かる文言で通知する。
  const prepared = await prepareImageForUpload(file);
  if (!prepared.ok) {
    dispatcher.discardAttachmentPlaceholder(objectId, overrideOpId);
    URL.revokeObjectURL(localUrl);
    showToast("error", prepared.message);
    return;
  }
  // 以降のアップロードは整形後のファイル（自動縮小・変換された場合は新しい File）を使う。
  const uploadFile = prepared.file;
  try {
    const presigned = await canvasApi.presignImageUpload({
      canvasId,
      objectId,
      contentType: uploadFile.type || "image/png",
      contentLength: uploadFile.size,
    });
    // S3 への直接 PUT。response.ok を検査して握り潰しを防ぐ（putToPresignedUrl 内で実施）。
    await putToPresignedUrl(presigned.uploadUrl, uploadFile, uploadFile.type || "image/png");
    const completed = await canvasApi.notifyUploadComplete(
      canvasId,
      presigned.bucket,
      presigned.key
    );
    if (completed.publicUrl) {
      dispatcher.finalizeAttachmentPlaceholder(objectId, overrideOpId, completed.publicUrl);
      URL.revokeObjectURL(localUrl);
    } else {
      // 公開 URL が無い環境（dev/mock）では共有できない。本人のローカルプレビューと
      // 他参加者側の仮表示はそのまま残す（localUrl は本人表示で使い続けるため revoke しない）。
      console.warn(
        "[upload] 公開 URL が未設定のため画像を共有できません（ATTACHMENTS_PUBLIC_BASE_URL 未設定）"
      );
    }
  } catch (e) {
    console.warn("[upload] 画像アップロードに失敗", e);
    dispatcher.discardAttachmentPlaceholder(objectId, overrideOpId);
    URL.revokeObjectURL(localUrl);
    showToast("error", describeUploadError("image", e));
  }
}

async function uploadAndPlaceVideo(
  canvasApi: CanvasApiClient,
  canvasId: string,
  dispatcher: CanvasOpDispatcher,
  file: File,
  position: { x: number; y: number },
  showToast: ShowToast
): Promise<void> {
  // プレースホルダ作成前に検証する。動画はクライアント変換できないため、ブラウザで再生可能な形式は
  // 全て受理し、再生不可・空・上限超過のみ原因の分かる専用メッセージで弾く（汎用トーストに潰さない）。
  const validation = validateVideoUpload(file);
  if (!validation.ok) {
    showToast("error", validation.message);
    return;
  }
  const placed = await placeVideoPlaceholder(dispatcher, file, position);
  if (!placed) return;
  const { objectId, overrideOpId, localUrl } = placed;
  try {
    const presigned = await canvasApi.presignVideoUpload({
      canvasId,
      objectId,
      contentType: file.type || "video/mp4",
      contentLength: file.size,
    });
    // 画像と同様、putToPresignedUrl 内で response.ok を検査して握り潰しを防ぐ。
    await putToPresignedUrl(presigned.uploadUrl, file, file.type || "video/mp4");
    const completed = await canvasApi.notifyUploadComplete(
      canvasId,
      presigned.bucket,
      presigned.key
    );
    if (completed.publicUrl) {
      dispatcher.finalizeAttachmentPlaceholder(objectId, overrideOpId, completed.publicUrl);
      URL.revokeObjectURL(localUrl);
    } else {
      console.warn(
        "[upload] 公開 URL が未設定のため動画を共有できません（ATTACHMENTS_PUBLIC_BASE_URL 未設定）"
      );
    }
  } catch (e) {
    console.warn("[upload] 動画アップロードに失敗", e);
    dispatcher.discardAttachmentPlaceholder(objectId, overrideOpId);
    URL.revokeObjectURL(localUrl);
    showToast("error", describeUploadError("video", e));
  }
}

interface PlacedAttachment {
  readonly objectId: string;
  readonly overrideOpId: string;
  readonly localUrl: string;
}

async function placeImagePlaceholder(
  dispatcher: CanvasOpDispatcher,
  file: File,
  position: { x: number; y: number }
): Promise<PlacedAttachment | null> {
  const localUrl = URL.createObjectURL(file);
  // SVG は width/height 未指定（viewBox のみ）でも本来の縦横比を保てるよう、専用の寸法解決を使う。
  const dimensions =
    file.type === "image/svg+xml"
      ? await readSvgSize(file, localUrl)
      : await readImageSize(localUrl);
  const p = snapPointIfGridEnabled({
    x: position.x - dimensions.w / 2,
    y: position.y - dimensions.h / 2,
  });
  const frame = clampFrameToCanvas({
    x: p.x,
    y: p.y,
    w: dimensions.w,
    h: dimensions.h,
  });
  // 他参加者へ配信する仮表示用 src（超圧縮 LQIP。生成不可なら透明プレースホルダ）。
  const lqip = await generateImageLqipDataUrl(localUrl);
  // 永続化用の placeholderSrc は、有用な LQIP が生成できたときだけ設定する
  // （透明 1x1 プレースホルダは背景として無意味なため）。
  // サーバ永続化により、リロード後や別セッションの参加者でもぼかしフォールバックが利用できる。
  const persistedPlaceholder = lqip !== PENDING_ATTACHMENT_SRC ? lqip : undefined;
  const placed = dispatcher.createAttachmentPlaceholder(
    {
      type: "image",
      ...frame,
      src: lqip,
      alt: file.name,
      ...(persistedPlaceholder ? { placeholderSrc: persistedPlaceholder } : {}),
    },
    localUrl
  );
  if (!placed) {
    URL.revokeObjectURL(localUrl);
    return null;
  }
  return { objectId: placed.object.id, overrideOpId: placed.overrideOpId, localUrl };
}

async function placeVideoPlaceholder(
  dispatcher: CanvasOpDispatcher,
  file: File,
  position: { x: number; y: number }
): Promise<PlacedAttachment | null> {
  const localUrl = URL.createObjectURL(file);
  const p = snapPointIfGridEnabled({ x: position.x - 240, y: position.y - 135 });
  const frame = clampFrameToCanvas({
    x: p.x,
    y: p.y,
    w: 480,
    h: 270,
  });
  // 動画はフレーム抽出を行わず、他参加者へは透明プレースホルダ（ローディング枠）を配信する。
  const placed = dispatcher.createAttachmentPlaceholder(
    {
      type: "video",
      ...frame,
      src: PENDING_ATTACHMENT_SRC,
      mimeType: file.type || "video/mp4",
    },
    localUrl
  );
  if (!placed) {
    URL.revokeObjectURL(localUrl);
    return null;
  }
  return { objectId: placed.object.id, overrideOpId: placed.overrideOpId, localUrl };
}

// 固有サイズを持たない SVG（width/height 未指定）で naturalWidth/Height が 0 になった場合の既定サイズ。
const SVG_FALLBACK_SIZE = { w: 320, h: 240 } as const;

// SVG ファイルから固有の縦横比を読み取り、表示サイズ（最長辺 = IMAGE_DEFAULT_MAX）を返す。
// width/height 属性が無く viewBox のみの SVG でも、本来の縦横比を保ったまま配置できる。
// 解析に失敗した場合は通常の readImageSize（<img> の固有サイズ / 既定値）へフォールバックする。
async function readSvgSize(file: File, localUrl: string): Promise<{ w: number; h: number }> {
  try {
    const text = await file.text();
    const doc = new DOMParser().parseFromString(text, "image/svg+xml");
    const svg = doc.documentElement;
    if (
      svg.nodeName.toLowerCase() === "svg" &&
      doc.getElementsByTagName("parsererror").length === 0
    ) {
      const ratio = readSvgIntrinsicRatio(svg);
      if (ratio) {
        // ベクターは拡大しても劣化しないため、最長辺を IMAGE_DEFAULT_MAX に合わせて配置する。
        const scale = IMAGE_DEFAULT_MAX / Math.max(ratio.w, ratio.h);
        return { w: ratio.w * scale, h: ratio.h * scale };
      }
    }
  } catch {
    // パース失敗時はフォールバックする。
  }
  return readImageSize(localUrl);
}

// SVG 要素から固有の縦横（width/height 属性、無ければ viewBox の幅・高さ）を取り出す。
function readSvgIntrinsicRatio(svg: Element): { w: number; h: number } | null {
  const parseLength = (value: string | null): number | null => {
    if (!value) return null;
    // 単位なし or px のみを数値として扱う（%, em 等は固有サイズとして使わない）。
    const match = /^\s*([0-9]*\.?[0-9]+)\s*(px)?\s*$/i.exec(value);
    if (!match) return null;
    const n = Number.parseFloat(match[1] ?? "");
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  const wAttr = parseLength(svg.getAttribute("width"));
  const hAttr = parseLength(svg.getAttribute("height"));
  if (wAttr !== null && hAttr !== null) return { w: wAttr, h: hAttr };
  const viewBox = svg.getAttribute("viewBox");
  if (viewBox) {
    const parts = viewBox.split(/[\s,]+/);
    const vw = Number.parseFloat(parts[2] ?? "");
    const vh = Number.parseFloat(parts[3] ?? "");
    if (Number.isFinite(vw) && Number.isFinite(vh) && vw > 0 && vh > 0) {
      return { w: vw, h: vh };
    }
  }
  return null;
}

function readImageSize(url: string): Promise<{ w: number; h: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      // SVG で width/height が未指定だと naturalWidth/Height が 0 になることがある。
      // その場合は 0 サイズで作成されないよう既定サイズへフォールバックする。
      if (img.naturalWidth === 0 || img.naturalHeight === 0) {
        resolve({ ...SVG_FALLBACK_SIZE });
        return;
      }
      const ratio = Math.min(1, IMAGE_DEFAULT_MAX / Math.max(img.naturalWidth, img.naturalHeight));
      resolve({ w: img.naturalWidth * ratio, h: img.naturalHeight * ratio });
    };
    img.onerror = () => resolve({ w: 320, h: 240 });
    img.src = url;
  });
}
